'use client';

import {
  focusGroupKeyUX,
  focusGroupPolyfill,
  hiddenKeyUX,
  jumpKeyUX,
  pressKeyUX,
  startKeyUX,
} from 'keyux';
import {AnimatePresence, motion} from 'motion/react';
import {ReactNode, useEffect, useMemo} from 'react';
import {useLocation} from 'react-use';

import {cn, getMotionProps} from '../../libs';
import {modalStore} from './modalStore';

export interface ModalProviderProps {
  children: ReactNode;
}

// TODO: move to global ui provider

if (typeof window !== 'undefined') {
  // ^- for plasmo extension
  startKeyUX(window, [
    focusGroupKeyUX(),
    focusGroupPolyfill(),
    pressKeyUX('is-pressed'),
    jumpKeyUX(),
    hiddenKeyUX(),
  ]);
}

const overlayMotionProps = getMotionProps({
  initial: {opacity: 0},
  animate: {opacity: 1},
  exit: {opacity: 0},
});

const entryMotionProps = getMotionProps({
  initial: {y: -4, scale: 0.98, opacity: 0},
  animate: {y: 0, scale: 1, opacity: 1},
  exit: {y: -4, scale: 0.98, opacity: 0},
});

export const ModalProvider = ({children}: ModalProviderProps) => {
  const location = useLocation();
  const {modals, reset} = modalStore();
  const current = useMemo(() => modals.at(-1), [modals]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    reset();
  }, [location]);

  useEffect(() => {
    if (current) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [current]);

  return (
    <>
      {children}
      <AnimatePresence mode="wait">
        {current && (
          <div
            key={`modal-${current.id}`}
            className="fixed top-0 left-0 z-[2000] flex size-full items-center justify-center overflow-auto"
          >
            <motion.div
              {...overlayMotionProps}
              className={cn(
                'absolute top-0 left-0 z-[-1] size-full',
                !current.params.removeBackground && 'bg-[rgba(0,0,0,0.8)]',
              )}
            />
            <motion.div
              {...(current.params.reduceMotion ? overlayMotionProps : entryMotionProps)}
              className="flex w-full items-center justify-center p-8"
            >
              {current.element}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
